import { pageViewTypes } from "@constants/tracking";
import { useSessionConfigDialog } from "@contexts/DialogContext/dialogs/SessionConfigDialog";
import { useExamContext } from "@features/exam/contexts/ExamContext";
import LegacyReportDownload from "@features/report/components/print/LegacyReportDownload";
import { useLegacyReportDownload } from "@features/report/hooks/useLegacyReportDownload";
import { getShowStudyPackViews } from "@helpers/exams";
import useAnalytics from "@hooks/useAnalytics";
import { useAppStore } from "@hooks/useAppStore";
import { ConfirmDialog } from "@memorang/ui/components/ConfirmDialog";
import ProgressApplet from "@memorang/ui/components/ProgressApplet";
import { useDevice } from "@memorang/ui/useDevice";
import { router } from "expo-router";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useProgressCards } from "../hooks/useProgressCards";
import { NestedExamChipsContainer } from "./NestedExamChipsContainer";

const TestPrepProgressContainer = () => {
	const [infoDialog, setInfoDialog] = useState<{
		title: string;
		body: string;
	} | null>(null);
	const {
		downloadingId,
		handleDownload: handleLegacyReportDownload,
		clearDownload,
	} = useLegacyReportDownload();
	const { isMobile } = useDevice();
	const { showSessionConfig } = useSessionConfigDialog();
	const { t } = useTranslation();

	const appFont = useAppStore((state) => state.app.font) || "opensans";
	const showPracticeTests = useAppStore((state) => state.app.showPracticeTests);

	const {
		currentExamContext: { studyPackCollectionId },
	} = useExamContext();

	const memoizedAnalyticsParams = useMemo(
		() => ({ type: pageViewTypes.progress }),
		[],
	);
	useAnalytics(memoizedAnalyticsParams);

	const {
		data,
		isLoading,
		needsImprovementProgressData,
		weakTagIds,
		showEmptyState,
	} = useProgressCards();

	const showStudyPackViews = getShowStudyPackViews(studyPackCollectionId);

	const handleClickCard = (title: string) => {
		router.push({
			pathname: "/progress/detailed-list/mastery",
			params: { type: title },
		});
	};

	const handleClickStudy = (tagIds?: string[]) => {
		const selectedTagIds = tagIds?.length ? tagIds : weakTagIds;
		showSessionConfig({
			selectedTagIds: selectedTagIds.map(Number),
			studyRecommendedTopics: true,
		});
	};

	const handleClickInfo = (body: string, title: string) => {
		if (isMobile) {
			router.push({
				pathname: "/info",
				params: { body, title },
			});
			return;
		}

		setInfoDialog({ title, body });
	};

	return (
		<>
			<ProgressApplet
				needsImprovement={data.needsImprovement}
				needsImprovementTagType={data.needsImprovementTagType}
				masteryTagsProgressData={data.masteryTags}
				needsImprovementProgressData={needsImprovementProgressData}
				progressPageSummaryCardData={data.summary}
				practiceTestsCardsData={data.practiceTests}
				legacyTestsCardsData={data.legacyTests}
				showPracticeTests={showPracticeTests}
				showStudyPackViews={showStudyPackViews}
				handleClickCard={handleClickCard}
				handleClickStudy={handleClickStudy}
				handleClickInfo={handleClickInfo}
				appFont={appFont}
				handleNavigation={(numericId) =>
					router.push(`/session/${numericId}/report/`)
				}
				handleLegacyReportDownload={handleLegacyReportDownload}
				downloadingLegacyReportId={downloadingId}
				handleViewMorePracticeTests={() =>
					router.push("/progress/detailed-list/practice-tests")
				}
				handleViewMoreLegacyTests={() =>
					router.push("/progress/detailed-list/legacy-tests")
				}
				progressNeedsImprovementCtaLabel={t("progress.needsImprovement.cta")}
				emptyState={{
					title: t("emptyState.progress.title"),
					subtitle: t("emptyState.progress.subtitle"),
					cta: t("emptyState.progress.cta"),
					onAction: () => {
						if (showStudyPackViews) {
							showSessionConfig({});
						} else {
							router.push("/home");
						}
					},
				}}
				showEmptyState={showEmptyState}
				weakTagIds={weakTagIds}
				isLoading={isLoading}
				renderNestedExamChips={() => (
					<NestedExamChipsContainer
						disabled={isLoading}
						containerStyle={{ paddingVertical: 24 }}
					/>
				)}
			/>
			{downloadingId ? (
				<LegacyReportDownload
					sessionId={downloadingId}
					onDone={clearDownload}
				/>
			) : null}
			{infoDialog ? (
				<ConfirmDialog
					visible={!!infoDialog}
					title={infoDialog.title}
					body={infoDialog.body}
					dismissCallback={() => setInfoDialog(null)}
					confirmCallback={() => setInfoDialog(null)}
					confirmCta={t("common.ok")}
				/>
			) : null}
		</>
	);
};

export default TestPrepProgressContainer;
